import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, createSlice } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IParent, defaultValue } from 'app/shared/model/parent.model';
import { IUserParent, defaultParentValue } from 'app/shared/model/user-parent.model';
const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IParent>,
  authorities: [] as any[],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
  userParents: [] as ReadonlyArray<IUserParent>,
  userParent: defaultParentValue,
};

const apiUrl = 'api/parents';

// Actions

export const getParents = createAsyncThunk('parent/fetch_entity_list', async (entity: any) => {
  let firstName, lastName;
  let searchUrl = '';
  let requestUrl = '';
  let activated;
  let telephone;

  if (entity.firstName) {
    firstName = '&firstName=' + entity.firstName;
    searchUrl += firstName;
  }
  if (entity.lastName) {
    lastName = '&lastName=' + entity.lastName;
    searchUrl += lastName;
  }
  if (entity.telephone) {
    telephone = '&telephone=' + entity.telephone;
    searchUrl += telephone;
  }
  if (entity.activated) {
    activated = '&activated=' + entity.activated;
    searchUrl += activated;
  }
  if (!entity.firstName && !entity.lastName && !entity.activated && !entity.telephone) {
    requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}${searchUrl ? '&' : '?'}${searchUrl}`;
  }
  return axios.get<IUserParent[]>(requestUrl);
});

export const getParent = createAsyncThunk(
  'parent/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IParent>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'parent/create_entity',
  async (entity: IParent, thunkAPI) => {
    const result = await axios.post<IParent>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getParents({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'parent/update_entity',
  async (entity: IParent, thunkAPI) => {
    const result = await axios.put<IParent>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getParents({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'parent/partial_update_entity',
  async (entity: IParent, thunkAPI) => {
    const result = await axios.patch<IParent>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getParents({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'parent/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IParent>(requestUrl);
    thunkAPI.dispatch(getParents({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateStatusParent = createAsyncThunk(
  'parent/update_status_parent',
  async (entity: IParent, thunkAPI) => {
    const result = await axios.post<IParent>(`${apiUrl}/${entity.id}?status=${entity.activated}`);
    thunkAPI.dispatch(getParent(entity.id));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type ParentState = Readonly<typeof initialState>;

export const ParentSlice = createSlice({
  name: 'parent',
  initialState: initialState as ParentState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getParent.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getParents), (state, action) => {
        return {
          ...state,
          loading: false,
          userParents: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity, updateStatusParent), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getParents, getParent), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity, updateStatusParent), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ParentSlice.actions;
export default ParentSlice.reducer;
